import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Toolbar,
  Divider,
  Typography,
  Menu,
  MenuItem,
  Stack,
  Avatar,
  Tooltip,
  ListItemIcon,
  Badge,
  ButtonBase,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import LogoutIcon from "@mui/icons-material/Logout";

import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import { useDispatch, useSelector } from "react-redux";
import { setDrawer } from "../../../app/slices/appSlice";
import { User } from "../../../app/slices/userSlice";
import { useNavigate } from "react-router";
import { CgProfile } from "react-icons/cg";
import { MdKeyboardArrowDown, MdNotificationsNone } from "react-icons/md";
import { BRAND, BRAND_GRADIENT } from "../../../components/MobileList";

const drawerWidth = 220;

const iconButtonSx = {
  borderRadius: "12px",
  width: 42,
  height: 42,
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.15)",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    // Only the persistent (md+) drawer pushes the bar over; below md it is a
    // temporary overlay, so the bar must stay full width.
    [theme.breakpoints.up("md")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  }),
}));

const Appbar = () => {
  const navigate = useNavigate();

  const leftDrawerOpened = useSelector((state) => state.app.is_drawer_opened);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(User.userInfo());
  }, []);

  const userInfo = useSelector((state) => state.user.userInfo);
  const openStatistic = useSelector((state) => state.notification.open);

  const [open, setOpen] = useState(false);
  const anchorEl = useRef();
  return (
    <Box>
      <AppBar
        position="fixed"
        elevation={0}
        open={leftDrawerOpened}
        sx={{
          background: BRAND_GRADIENT,
          color: "#fff",
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important", gap: 1.5 }}>
          <IconButton
            onClick={() => dispatch(setDrawer())}
            aria-label="open drawer"
            sx={iconButtonSx}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography fontWeight="bold" fontSize={16} lineHeight={1.3}>
              أهلاً، {userInfo?.user_name ?? ""}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: { xs: "none", sm: "block" }, opacity: 0.8 }}
            >
              {new Date().toLocaleDateString("ar-IQ-u-nu-latn", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </Typography>
          </Box>
          <Tooltip title="طلبات الجملة الجديدة">
            <IconButton
              onClick={() => navigate("/app/wholesale/orders")}
              sx={iconButtonSx}
            >
              <Badge
                badgeContent={openStatistic?.j_new_orders ?? 0}
                color="error"
              >
                <MdNotificationsNone size={22} />
              </Badge>
            </IconButton>
          </Tooltip>
          <Box>
            {userInfo && (
              <Stack
                ref={anchorEl}
                component={ButtonBase}
                onClick={() => setOpen(true)}
                direction="row"
                spacing={1.25}
                alignItems="center"
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                  p: 0.5,
                  pr: 1.25,
                  borderRadius: "14px",
                  border: "1px solid rgba(255,255,255,0.3)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                }}
              >
                <Avatar
                  src={userInfo.logo || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    bgcolor: "#fff",
                    color: BRAND,
                    fontSize: 16,
                    fontWeight: "bold",
                  }}
                >
                  {userInfo.user_name?.[0]?.toUpperCase() ?? "M"}
                </Avatar>
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    textAlign: "start",
                  }}
                >
                  <Typography fontSize={13} fontWeight={600} lineHeight={1.3}>
                    {userInfo.user_name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {userInfo.email}
                  </Typography>
                </Box>
                <MdKeyboardArrowDown size={18} />
              </Stack>
            )}
            <Menu
              sx={{ direction: "ltr" }}
              anchorEl={() => anchorEl.current}
              keepMounted
              id="account-menu"
              open={open}
              onClose={() => setOpen(false)}
              onClick={() => setOpen(false)}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 4px 16px rgba(16,24,40,0.12))",
                  borderRadius: "12px",
                  minWidth: 200,
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    left: 33,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem
                onClick={() => {
                  navigate("/app/user/Profile");
                }}
              >
                <ListItemIcon>
                  <CgProfile size={25} />
                </ListItemIcon>
                الصفحة الشخصية
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={async () => {
                  await dispatch(User.logout());
                  navigate("/login");
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                تسجيل خروج
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

export default Appbar;
